import { Controller } from '@hotwired/stimulus';

/**
 * API-backed autocomplete that always submits the selected text (city)
 * instead of an ID. The hidden "free text" field carries the submitted
 * param name; the hidden "ID" field stays disabled/empty.
 *
 * External control:
 *  - Dispatch on root element:
 *      "autocomplete:enter-free"
 *      "autocomplete:enter-select"
 *  - Or call public methods:
 *      controller.enterFreeMode()
 *      controller.enterSelectMode()
 *
 * API contract:
 *   GET <url>?q=<query>&limit=<n>[&debug=1]
 *   Returns { query, results, took_ms } plus { normalizedQuery, count } if debug=1
 *   Each result:
 *     { id:number, display:string, zip:string|null, score:number,
 *       highlights:[{ start, end }] }
 */
export default class extends Controller {
  static targets = ['input', 'menu', 'hiddenId', 'hiddenFreeText'];

  static values = {
    url: String,
    limit: { type: Number, default: 5 },
    noresultText: String,
    freeTextName: String, // kept for back-compat (not used to submit)
    idName: String, // actual submitted param name (string value)
    initialDisplay: String,
    initialId: String, // ignored for submission; kept in events only
  };

  // === Lifecycle ===========================================================

  connect() {
    this.state = {
      open: false,
      items: [],
      activeIndex: -1,
      mode: 'select', // or "free"
    };

    // Prefill visible field
    if (this.initialDisplayValue) {
      this.inputTarget.value = this.initialDisplayValue;
    }

    // Always submit a string under provided param name (idNameValue)
    this.hiddenFreeTextTarget.disabled = false;
    this.hiddenFreeTextTarget.name = this.idNameValue;
    this.hiddenFreeTextTarget.value = this.initialDisplayValue || '';

    // Ensure ID field is never submitted
    this.hiddenIdTarget.disabled = true;
    this.hiddenIdTarget.name = '';
    this.hiddenIdTarget.value = '';

    // Bindings
    this._onInput = this._debounce(this._onInput.bind(this), 140);
    this._onKeydown = this._onKeydown.bind(this);
    this._onClickOutside = this._onClickOutside.bind(this);

    this.inputTarget.addEventListener('input', this._onInput);
    this.inputTarget.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onClickOutside);

    // External events API
    this._onEnterFree = () => this.enterFreeMode();
    this._onEnterSelect = () => this.enterSelectMode();

    this.element.addEventListener('autocomplete:enter-free', this._onEnterFree);
    this.element.addEventListener(
      'autocomplete:enter-select',
      this._onEnterSelect
    );

    // Abort controller for fetches
    this.abortCtrl = null;
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this._onInput);
    this.inputTarget.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onClickOutside);

    this.element.removeEventListener(
      'autocomplete:enter-free',
      this._onEnterFree
    );
    this.element.removeEventListener(
      'autocomplete:enter-select',
      this._onEnterSelect
    );

    if (this.abortCtrl) this.abortCtrl.abort();
  }

  // === Network ============================================================

  async _search(query) {
    if (this.abortCtrl) this.abortCtrl.abort();
    this.abortCtrl = new AbortController();

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('limit', String(this.limitValue));

    try {
      const res = await fetch(url.toString(), {
        signal: this.abortCtrl.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return Array.isArray(json.results) ? json.results : [];
    } catch (_e) {
      // Fail-soft: empty list on error
      return [];
    }
  }

  // === Public API =========================================================

  /** Forces free mode (string submission) and keeps current input value. */
  enterFreeMode() {
    const val = (this.inputTarget.value || '').trim();
    this._enterFreeMode(val);
  }

  /**
   * Forces select mode (autocomplete active). We still submit a string,
   * never an ID. Optionally refreshes suggestions for current query.
   */
  enterSelectMode() {
    this._disableFreeText();
    this.state.mode = 'select';

    const q = (this.inputTarget.value || '').trim();
    if (q.length > 0) {
      this._search(q).then((results) => {
        this.state.items = results;
        this._renderMenu(q);
        if (results.length > 0) this._open();
      });
    } else {
      this._clearResults();
      this._close();
    }
  }

  // === UI / Handlers ======================================================

  async _onInput(e) {
    const q = e.target.value.trim();

    // Always keep submitted string in sync
    this.hiddenFreeTextTarget.value = q;

    if (q.length === 0) {
      this._clearResults();
      this._close();
      return;
    }

    // Do not fetch if explicitly in free mode
    if (this.state.mode === 'free') return;

    const results = await this._search(q);
    this.state.items = results;
    this._renderMenu(q);
    this._open();
  }

  _renderMenu(query) {
    const items = [...this.state.items];
    items.push({
      id: null,
      display: this.noresultTextValue,
      zip: null,
      _sentinel: 'no-result',
    });

    const html = items
      .map((item, i) => {
        const isSentinel = !!item._sentinel;
        const labelHtml = isSentinel
          ? this._escape(item.display || '')
          : this._highlight(item.display || '', item.highlights || []);
        const zipHtml = item.zip
          ? `<small class="ms-2 flex-shrink-0 text-muted">(${this._escape(
              item.zip
            )})</small>`
          : '';
        const sentinelCls = isSentinel ? ' text-muted' : '';

        return [
          `<button type="button"`,
          ` class="dropdown-item d-flex justify-content-between${sentinelCls}"`,
          ` role="option"`,
          ` data-index="${i}"`,
          ` data-sentinel="${isSentinel}">`,
          `<span class="text-truncate">${labelHtml}</span>`,
          zipHtml,
          `</button>`,
        ].join(' ');
      })
      .join('');

    this.menuTarget.innerHTML = html;

    this.menuTarget.querySelectorAll('[data-index]').forEach((el) =>
      el.addEventListener('click', (ev) => {
        const idx = Number(ev.currentTarget.dataset.index);
        const isSentinel = ev.currentTarget.dataset.sentinel === 'true';
        if (isSentinel) {
          this._enterFreeMode(query);
        } else {
          this._choose(idx);
        }
      })
    );

    this.state.activeIndex = -1;
  }

  _choose(idx) {
    const item = this.state.items[idx];
    if (!item) return;

    const label = item.display || '';
    this.inputTarget.value = label;
    this.hiddenFreeTextTarget.value = label;

    // Keep ID field disabled/empty (never submitted)
    this.hiddenIdTarget.disabled = true;
    this.hiddenIdTarget.name = '';
    this.hiddenIdTarget.value = '';

    this.state.mode = 'select';
    this._close();

    // Event still exposes ID for analytics/debug (not submitted)
    this.dispatch('select', {
      detail: {
        item,
        display: label,
        id: item.id,
        zip: item.zip ?? null,
        mode: 'select',
        query: this.inputTarget.value,
      },
      bubbles: true,
    });
  }

  _enterFreeMode(prefill) {
    this.state.mode = 'free';
    this._enableFreeText(prefill);
    this._close();
  }

  _enableFreeText(val) {
    // Submit string under provided param name
    this.hiddenFreeTextTarget.disabled = false;
    this.hiddenFreeTextTarget.name = this.idNameValue;
    this.hiddenFreeTextTarget.value = val;

    // Ensure ID field is never submitted
    this.hiddenIdTarget.disabled = true;
    this.hiddenIdTarget.name = '';
    this.hiddenIdTarget.value = '';

    this.inputTarget.setAttribute('aria-expanded', 'false');
  }

  _disableFreeText() {
    // Always submit string; never switch back to ID submission
    this.hiddenFreeTextTarget.disabled = false;
    this.hiddenFreeTextTarget.name = this.idNameValue;

    this.hiddenIdTarget.disabled = true;
    this.hiddenIdTarget.name = '';
    this.hiddenIdTarget.value = '';

    this.state.mode = 'select';
  }

  _open() {
    if (this.state.open) return;
    this.menuTarget.classList.add('show');
    this.inputTarget.setAttribute('aria-expanded', 'true');
    this.state.open = true;
  }

  _close() {
    if (!this.state.open) return;
    this.menuTarget.classList.remove('show');
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.state.open = false;
  }

  _clearResults() {
    this.menuTarget.innerHTML = '';
    this.state.items = [];
    this.state.activeIndex = -1;
  }

  _onClickOutside(ev) {
    if (!this.element.contains(ev.target)) this._close();
  }

  _onKeydown(ev) {
    if (!this.state.open && ['ArrowDown', 'ArrowUp'].includes(ev.key)) {
      if (this.state.items.length > 0) this._open();
      return;
    }

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        this._move(1);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        this._move(-1);
        break;
      case 'Enter':
        if (!this.state.open) return;
        ev.preventDefault();
        if (this.state.activeIndex === -1) return;
        if (this.state.activeIndex >= this.state.items.length) return;
        this._choose(this.state.activeIndex);
        break;
      case 'Escape':
        this._close();
        break;
      default:
        break;
    }
  }

  _move(delta) {
    const max = this.state.items.length;
    if (max === 0) return;

    let next = this.state.activeIndex + delta;
    if (next < 0) next = max - 1;
    if (next >= max) next = 0;

    this.state.activeIndex = next;

    this.menuTarget
      .querySelectorAll('[data-index]')
      .forEach((el) => el.classList.remove('active'));
    const btn = this.menuTarget.querySelector(`[data-index="${next}"]`);
    if (btn) btn.classList.add('active');
  }

  // === Utils ==============================================================

  _debounce(fn, ms) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  }

  _escape(s) {
    return (s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  }

  /**
   * Insert <mark> tags around highlight ranges.
   * Each highlight is an object { start, end } with UTF-16 indices.
   */
  _highlight(display, highlights) {
    if (!Array.isArray(highlights) || highlights.length === 0) {
      return this._escape(display);
    }

    const sorted = [...highlights].sort((a, b) => a.start - b.start);
    let html = '';
    let lastIndex = 0;

    for (const { start, end } of sorted) {
      const safeStart = Math.max(0, start);
      const safeEnd = Math.min(display.length, end);

      html += this._escape(display.slice(lastIndex, safeStart));
      html += `<mark>${this._escape(display.slice(safeStart, safeEnd))}</mark>`;
      lastIndex = safeEnd;
    }

    html += this._escape(display.slice(lastIndex));
    return html;
  }
}
